import React, { useState } from "react";
import TopNav from "../components/TopNav";
import AppShell from "../components/AppShell";
import SEO from "../components/SEO";
import Footer from "../components/Footer";
import { api, useAuth } from "../context/AuthContext";
import { Handshake, Send, CheckCircle2, Loader2 } from "lucide-react";

const TYPES = [
  { v: "streamer", label: "Streamer / Content" },
  { v: "affiliate", label: "Affiliate" },
  { v: "studio", label: "Studio collab" },
  { v: "sponsorship", label: "Sponsorship" },
  { v: "other", label: "Other" },
];

export default function Partner() {
  const { user, loading } = useAuth();
  const [f, setF] = useState({ name: "", email: "", company: "", partnership_type: "streamer", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSending(true);
    try {
      await api.post("/partner/message", f);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setSending(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/60"><Loader2 className="animate-spin" /></div>;

  const frame = (inner, nav = <TopNav variant="app" />) => user
    ? <AppShell>{inner}</AppShell>
    : <div className="relative min-h-screen text-white" data-testid="partner-page">{nav}{inner}</div>;

  return frame(
    <>
      <SEO
        title="Partner with Gumble — streamers, affiliates, studios"
        description="Work with Gumble Studio: streamer sponsorships, affiliate deals, studio collabs and game development services."
        path="/partner"
      />
      <div className={`${user ? "pt-10 md:pt-14" : "pt-28"} px-6 md:px-10 max-w-4xl mx-auto pb-20`}>
        <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] text-white/40">
          <Handshake className="w-3.5 h-3.5" /> Partnerships
        </div>
        <h1 className="mt-2 text-4xl md:text-6xl font-black tracking-[-0.03em]">Partner with Gumble</h1>
        <p className="mt-4 max-w-2xl text-white/60 text-[15px] leading-relaxed">
          Streamers, affiliates, other studios and sponsors — if you want to build something together, tell us what you have in mind. We read every message.
        </p>

        {done ? (
          <div className="mt-14 glass rounded-3xl p-10 text-center" data-testid="partner-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto" />
            <div className="mt-4 text-2xl font-bold">Thanks — message received.</div>
            <p className="mt-2 text-white/60 text-[14px]">We'll reply from partnerships@gumble.online within 2 business days.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-12 glass rounded-3xl p-7 md:p-10 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Your name">
                <input required data-testid="partner-name" value={f.name} onChange={(e) => set("name", e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-[14px] outline-none focus:border-[#0A84FF]/70" />
              </Field>
              <Field label="Email">
                <input required type="email" data-testid="partner-email" value={f.email} onChange={(e) => set("email", e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-[14px] outline-none focus:border-[#0A84FF]/70" />
              </Field>
              <Field label="Company / channel (optional)">
                <input data-testid="partner-company" value={f.company} onChange={(e) => set("company", e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-[14px] outline-none focus:border-[#0A84FF]/70" />
              </Field>
              <Field label="Partnership type">
                <select data-testid="partner-type" value={f.partnership_type} onChange={(e) => set("partnership_type", e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-[14px] outline-none focus:border-[#0A84FF]/70">
                  {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="What do you have in mind?">
              <textarea required data-testid="partner-message" value={f.message} onChange={(e) => set("message", e.target.value)} rows={6}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-[14px] outline-none focus:border-[#0A84FF]/70" />
            </Field>

            {error && <div className="text-red-300 text-[13px]">{error}</div>}

            <div className="flex justify-end">
              <button data-testid="partner-submit" disabled={sending}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A84FF] text-white text-[13.5px] font-semibold hover:bg-[#0077ED] disabled:opacity-50">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Sending..." : "Send message"}
              </button>
            </div>
          </form>
        )}
      </div>
      <Footer />
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-white/40 mb-2">{label}</div>
      {children}
    </label>
  );
}
